import React from "react";
import Selector from "@ui/Selector";
import {
  useReportingPeriod,
  useReportingPeriodOptions,
  DEFAULT_REPORTING_PERIOD,
} from "./useReportingPeriod";
import { useReportingChartPeriod } from "./useReportingChartPeriod";
import { DateTime, Duration, Interval } from "luxon";
import type { Reporting } from "../../../api/useReporting";
import { HIDE_ON_WIDGET_EXPORT } from "@ui/ExportWidgetButton";

function ReportingPeriodSelector({ reporting }: { reporting: Reporting }) {
  const reportingPeriodOptions = useReportingPeriodOptions(reporting.range);
  const [selectedReportingPeriod, setSelectedReportingPeriod] = useReportingPeriod({});
  const [reportingChartPeriod, setReportingChartPeriod] = useReportingChartPeriod(
    reporting,
    reporting.range,
  );

  const handleReportingPeriodChange = React.useCallback(
    (value: string) => {
      setReportingChartPeriod(previousPeriod => {
        if (!reporting.range) return previousPeriod;

        const maxDate = DateTime.fromISO(reporting.range.max);
        const minDate = DateTime.fromISO(reporting.range.min);
        const startOfLastYear = maxDate.startOf("year");
        const lastYearInterval = Interval.fromDateTimes(startOfLastYear, maxDate)
          .toDuration()
          .toString();

        const min = minDate.toMillis();
        const max = maxDate.toMillis();

        if (lastYearInterval === value) {
          return {
            from: Math.max(min, startOfLastYear.toMillis()),
            to: max,
          };
        }

        const duration = Duration.fromISO(value).toMillis();

        previousPeriod.from ??= min;
        previousPeriod.to ??= max;

        return {
          from: Math.max(
            min,
            DateTime.fromMillis(previousPeriod.to)
              .minus(Duration.fromISO(value).toObject())
              .toMillis(),
          ),
          to: max - min === duration ? max : previousPeriod?.to,
        };
      });
      setSelectedReportingPeriod(value);
    },
    [reporting.range, setReportingChartPeriod, setSelectedReportingPeriod],
  );

  React.useEffect(
    () => () => {
      setSelectedReportingPeriod(DEFAULT_REPORTING_PERIOD);
    },
    [reportingPeriodOptions, setSelectedReportingPeriod],
  );

  /**
   * When the reporting chart period change, we want to select the closest period option in selector.
   * Null if < 3 months.
   */
  React.useEffect(() => {
    /**
     * Extract millis from period selection value (e.g. "P3M" -> 7776000000 (3 months))
     */
    const options: [number, string][] = reportingPeriodOptions.map(({ value }) => [
      Duration.fromISO(value).toMillis(),
      value,
    ]);
    const chartPeriodInterval = reportingChartPeriod.to - reportingChartPeriod.from;
    /**
     * Find closest option.
     */
    const [, targetISO] = options.reduce((prev, curr) =>
      Math.abs(curr[0] - chartPeriodInterval) < Math.abs(prev[0] - chartPeriodInterval)
        ? curr
        : prev,
    );
    /**
     * If closest option is < 3 months, select null.
     * Otherwise, select closest option.
     * This is to avoid having a period selector with a 3 months option selected when the chart period is < 3 months.
     */
    setSelectedReportingPeriod(
      Duration.fromMillis(chartPeriodInterval).as("month") < 3 ? null : targetISO,
    );
  }, [
    selectedReportingPeriod,
    reportingChartPeriod,
    setSelectedReportingPeriod,
    reportingPeriodOptions,
  ]);

  return (
    <Selector
      options={reportingPeriodOptions}
      value={selectedReportingPeriod}
      onChange={handleReportingPeriodChange}
      className={HIDE_ON_WIDGET_EXPORT}
    />
  );
}

export default React.memo(ReportingPeriodSelector);
